// components/charts/SpendingTrendsChart.js
import React, { useMemo } from "react";
import {
  Box,
  CircularProgress,
  Typography,
  useTheme,
  Grid,
  Divider,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";

const SpendingTrendsChart = ({ transactionStats, loading }) => {
  const { t } = useTranslation();
  const theme = useTheme();

  // Define a green color for eSIM purchases
  const esimColor = "#2e7d32"; // Material UI green[800]

  const chartData = useMemo(() => {
    if (!transactionStats || transactionStats.length === 0) {
      return [];
    }

    // Group by date and calculate credit purchases and eSIM purchases
    const dataByDate = {};

    // First, initialize all dates to ensure we have entries even for days with zero transactions
    transactionStats.forEach((transaction) => {
      const date = new Date(transaction.created_at || transaction.createdAt)
        .toISOString()
        .split("T")[0];

      if (!dataByDate[date]) {
        dataByDate[date] = {
          date,
          credits: 0,
          esim: 0,
        };
      }
    });

    // Now process each transaction
    transactionStats.forEach((transaction) => {
      const date = new Date(transaction.created_at || transaction.createdAt)
        .toISOString()
        .split("T")[0];

      // Always use absolute amounts for the chart display
      const amount = Math.abs(Number(transaction.amount));

      if (transaction.type === "credit_purchase") {
        dataByDate[date].credits += amount;
      } else if (
        transaction.type === "credit_adjustment" &&
        Number(transaction.amount) > 0
      ) {
        dataByDate[date].credits += amount;
      } else if (transaction.type === "esim_purchase") {
        dataByDate[date].esim += amount;
      }
    });

    // Convert to array and sort by date
    return Object.values(dataByDate)
      .sort((a, b) => new Date(a.date) - new Date(b.date))
      .map((item) => ({
        ...item,
        date: new Date(item.date).toLocaleDateString(undefined, {
          month: "short",
          day: "numeric",
        }),
      }));
  }, [transactionStats]);

  // Calculate totals
  const totalCredits = useMemo(() => {
    return chartData.reduce((sum, item) => sum + item.credits, 0);
  }, [chartData]);

  const totalEsim = useMemo(() => {
    return chartData.reduce((sum, item) => sum + item.esim, 0);
  }, [chartData]);

  const CustomTooltip = ({ active, payload, label, dataKey, color }) => {
    if (active && payload && payload.length) {
      return (
        <Box
          sx={{
            bgcolor: "background.paper",
            p: 2,
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: 1,
            boxShadow: theme.shadows[3],
          }}
        >
          <Typography variant="subtitle2">{label}</Typography>
          <Typography
            variant="body2"
            sx={{
              color: color,
            }}
          >
            {dataKey === "credits"
              ? t("dashboard.creditPurchases")
              : t("dashboard.esimPurchases")}
            : ${payload[0].value.toFixed(2)}
          </Typography>
        </Box>
      );
    }
    return null;
  };

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height={300}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (chartData.length === 0) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height={300}
      >
        <Typography color="textSecondary">
          {t("dashboard.noTransactionData")}
        </Typography>
      </Box>
    );
  }

  return (
    <Grid container spacing={2}>
      {/* Credit Purchases Chart */}
      <Grid item xs={12}>
        <Box
          sx={{
            mb: 1,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography variant="subtitle1" fontWeight="medium">
            {t("dashboard.creditPurchases")}
          </Typography>
          <Typography
            variant="subtitle1"
            color="primary.main"
            fontWeight="bold"
          >
            ${totalCredits.toFixed(2)}
          </Typography>
        </Box>
        <Box height={220}>
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart
              data={chartData}
              margin={{ top: 10, right: 5, left: 5, bottom: 5 }}
            >
              <defs>
                <linearGradient id="colorCredits" x1="0" y1="0" x2="0" y2="1">
                  <stop
                    offset="5%"
                    stopColor={theme.palette.primary.main}
                    stopOpacity={0.8}
                  />
                  <stop
                    offset="95%"
                    stopColor={theme.palette.primary.main}
                    stopOpacity={0.1}
                  />
                </linearGradient>
              </defs>
              <CartesianGrid
                strokeDasharray="3 3"
                stroke={theme.palette.divider}
              />
              <XAxis
                dataKey="date"
                tick={{ fontSize: 12 }}
                stroke={theme.palette.text.secondary}
              />
              <YAxis
                tickFormatter={(value) => `$${value}`}
                tick={{ fontSize: 12 }}
                stroke={theme.palette.text.secondary}
              />
              <Tooltip
                content={(props) => (
                  <CustomTooltip
                    {...props}
                    dataKey="credits"
                    color={theme.palette.primary.main}
                  />
                )}
              />
              <Area
                type="monotone"
                dataKey="credits"
                stroke={theme.palette.primary.main}
                fillOpacity={1}
                fill="url(#colorCredits)"
                animationDuration={1500}
                name="credits"
              />
            </AreaChart>
          </ResponsiveContainer>
        </Box>
      </Grid>

      <Grid item xs={12}>
        <Divider sx={{ my: 1 }} />
      </Grid>

      {/* eSIM Purchases Chart */}
      <Grid item xs={12}>
        <Box
          sx={{
            mb: 1,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography variant="subtitle1" fontWeight="medium">
            {t("dashboard.esimPurchases")}
          </Typography>
          <Typography
            variant="subtitle1"
            sx={{ color: esimColor }}
            fontWeight="bold"
          >
            ${totalEsim.toFixed(2)}
          </Typography>
        </Box>
        <Box height={220}>
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart
              data={chartData}
              margin={{ top: 10, right: 5, left: 5, bottom: 5 }}
            >
              <defs>
                <linearGradient id="colorEsim" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={esimColor} stopOpacity={0.8} />
                  <stop offset="95%" stopColor={esimColor} stopOpacity={0.1} />
                </linearGradient>
              </defs>
              <CartesianGrid
                strokeDasharray="3 3"
                stroke={theme.palette.divider}
              />
              <XAxis
                dataKey="date"
                tick={{ fontSize: 12 }}
                stroke={theme.palette.text.secondary}
              />
              <YAxis
                tickFormatter={(value) => `$${value}`}
                tick={{ fontSize: 12 }}
                stroke={theme.palette.text.secondary}
              />
              <Tooltip
                content={(props) => (
                  <CustomTooltip {...props} dataKey="esim" color={esimColor} />
                )}
              />
              <Area
                type="monotone"
                dataKey="esim"
                stroke={esimColor}
                fillOpacity={1}
                fill="url(#colorEsim)"
                animationDuration={1500}
                name="esim"
              />
            </AreaChart>
          </ResponsiveContainer>
        </Box>
      </Grid>
    </Grid>
  );
};

export default SpendingTrendsChart;
